import '@app/lib/setupDayJs'
import '@app/styles/mention-styles.css'

import { RouterTransition } from '@app/components/layout/RouterTransition'
import { MantineProvider } from '@app/providers/MantineProvider'
import { RelayProvider } from '@app/providers/RelayProvider'
import { type AppProps } from 'next/app'
import Head from 'next/head'
import Script from 'next/script'
import { type Session } from 'next-auth'
import { SessionProvider } from 'next-auth/react'
import { type RecordMap } from 'relay-runtime/lib/store/RelayStoreTypes'
import { OrganizationStoreProvider } from '@app/providers/OrganizationProviders'
import { PreviousUrlProvider } from '@app/lib/usePreviousUrl'
import Bugsnag, { BugsnagErrorBoundary } from '@app/utils/bugsnag'
import { useEffect } from 'react'
import { ApolloProvider } from '@apollo/client'
import client from '@app/utils/createApolloClient'
import FcmManager from '@app/components/notifications/FcmManager'
import { cleanupLegacyDraftStorage } from '@app/lib/unsavedChanges/utils'
import '../styles/main.css'

interface PageProps {
  relayData: RecordMap | undefined
  session: Session | null
  token: string | null | undefined
}

const refetchIntervalSeconds = 300

const App = ({ Component, pageProps: { relayData, session, token, ...pageProps } }: AppProps<PageProps>) => {
  useEffect(() => {
    cleanupLegacyDraftStorage()
  }, [])

  // FullStory and Bugsnag useEffect
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkFSReady = setInterval(() => {
        if (window.FS) {
          // Set FullStory identity and properties
          window.FS('setIdentity', {
            consent: true,
            ['User ID']: session?.user?.id,
            Name: session?.user?.name,
            Email: session?.user?.email,
            properties: {
              organizationAdmin: session?.isOrganizationAdmin,
              platformAdmin: session?.isPlatformAdmin,
              companyName: session?.organization?.name,
              companyId: session?.organization?.id,
            },
          })

          window.FS('setProperties', {
            type: 'user',
            properties: {
              Name: session?.user?.name,
              displayName: session?.user?.name,
              Email: session?.user?.email,
              ['User ID']: session?.user?.id,
            },
          })

          // Clear the interval once FullStory is set up
          clearInterval(checkFSReady)
        }
      }, 100) // Check every 100ms

      // Bugsnag setup
      if (session?.user) {
        Bugsnag.setUser(session?.user.id, session?.user.email, session?.user.name)
        Bugsnag.addMetadata('Session Info', {
          userId: session?.user?.id,
          userName: session?.user?.name,
          companyName: session?.organization?.name,
          companyId: session?.organization?.id,
        })
        console.log('Bugsnag Initialized with user session info...')
      }

      // Clean up on component unmount
      return () => clearInterval(checkFSReady)
    } else {
      console.log('Document not loaded yet...')
    }
  }, [
    session?.isOrganizationAdmin,
    session?.isPlatformAdmin,
    session?.organization?.id,
    session?.organization?.name,
    session?.user,
  ])

  return (
    <>
      <BugsnagErrorBoundary>
        <SessionProvider
          refetchInterval={refetchIntervalSeconds}
          refetchOnWindowFocus
          session={session}
        >
          <RelayProvider
            relayData={relayData}
            token={token}
            userId={session?.user?.id}
          >
            <ApolloProvider client={client}>
              <MantineProvider>
                <PreviousUrlProvider>
                  <OrganizationStoreProvider>
                    <Head>
                      <title>SWAP PM</title>
                      <meta
                        name='viewport'
                        content='minimum-scale=1, initial-scale=1, width=device-width'
                      />
                      <link
                        rel='apple-touch-icon'
                        sizes='180x180'
                        href='/apple-touch-icon.png'
                      />
                      <link
                        rel='icon'
                        type='image/png'
                        sizes='32x32'
                        href='/favicon-32x32.png'
                      />
                      <link
                        rel='icon'
                        type='image/png'
                        sizes='16x16'
                        href='/favicon-16x16.png'
                      />
                      <link
                        rel='manifest'
                        href='/site.webmanifest'
                      />
                      <link
                        rel='mask-icon'
                        href='/safari-pinned-tab.svg'
                        color='#cccccc'
                      />
                      <meta
                        name='apple-mobile-web-app-title'
                        content='SWAP PM'
                      />
                      <meta
                        name='application-name'
                        content='SWAP PM'
                      />
                      <meta
                        name='msapplication-TileColor'
                        content='#ffffff'
                      />
                      <meta
                        name='theme-color'
                        content='#ffffff'
                      />
                    </Head>
                    <RouterTransition />
                    <FcmManager />
                    <Component {...pageProps} />
                  </OrganizationStoreProvider>
                </PreviousUrlProvider>
              </MantineProvider>
            </ApolloProvider>
          </RelayProvider>
        </SessionProvider>
      </BugsnagErrorBoundary>
    </>
  )
}

// noinspection JSUnusedGlobalSymbols
export default App
